import { Selection, CommandBar, DetailsList, DetailsListLayoutMode, Dialog, DialogContent, DialogType, IColumn, ICommandBarItemProps, ICommandBarStyles, IDialogContentProps, Link, mergeStyleSets, ScrollablePane, ScrollbarVisibility, Spinner, SpinnerSize, PanelType, IPanelStyles, Panel, ConstrainMode } from "@fluentui/react";
import dayjs from "dayjs";
import React from "react";
import { useSelector } from "react-redux";
import { useLazyDeleteAllVersionsQuery, useLazyDeleteVersionByLabelQuery, useLazyGetItemByUniqueIDVersionsQuery, useLazyGetItemVersionsQuery, useLazyRestoreVersionByLabelQuery, useLazyUnpublishItemQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { currentSharepointFolderUserPermissions } from "../../src/taskpane/redux/models/reduxModels";
import { getUserEffectivePermissions, getUserEffectivePermissionsFromMask } from "../../src/taskpane/services/SharePointRestServices";
import authModule from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { CMConfirmModal } from "./CMConfirmModal";
import { ILibraryPanelContext } from "../../src/taskpane/interfaces/ILibraryPanelContext";
import { menuItemIconStyles } from "../../src/taskpane/config/controlStyles";
import { ArrowClockwise20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/ArrowClockwise20Regular";
import { Delete20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Delete20Regular";
import { Dismiss20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Dismiss20Regular";
import { HistoryDismiss20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/HistoryDismiss20Regular";
import { Open20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Open20Regular";
import { StackOff20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/StackOff20Regular";

const classNames = mergeStyleSets({
    wrapper: {
        height: "300px",
        position: "relative",
    }
});

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 10,
        paddingRight: 10
    }
}

const commandBarStyle: Partial<ICommandBarStyles> = {
    root: {
        borderTop: '1px solid var(--db-border, rgb(237, 235, 233))',
        borderBottom: '1px solid var(--db-border, rgb(237, 235, 233))',
        paddingLeft: 0,
        paddingRight: 10,
        paddingBottom: 3,
        paddingTop: 3
    }
};

const formatBytes = (bytes: number, decimals: number = 2): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function _copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
    const key = columnKey as keyof T;
    return items.slice(0).sort((a: T, b: T) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
};

const _getKey = (item: any, index?: number): string => {
    return item?.key;
};

export interface ICMVersionHistoryModalProps {
    selectedItem?: any;
    selectedAddinItem?: any;
    showDialog: boolean;
    isInOneDrive: boolean;
    closeDialog: () => void;
    // Explicit target library (cross-library aggregate view); falls back to the
    // current directory when absent.
    libraryContext?: ILibraryPanelContext;
}

export const CMVersionHistoryModal = ({ selectedItem, selectedAddinItem, showDialog, isInOneDrive, closeDialog, libraryContext }: ICMVersionHistoryModalProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);

    const legacyWebUrl = isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;
    const targetWebUrl = libraryContext?.webUrl ?? legacyWebUrl;
    const targetListId = libraryContext?.sharePointListId ?? currentSharepointFolder?.sharepointListId;
    // Minor-version affordances must reflect the TARGET library's settings, not the
    // last-browsed folder's — the aggregate view passes them in the context.
    const targetEnableMinorVersions = libraryContext ? libraryContext.enableMinorVersions === true : currentSharepointFolder?.enableMinorVersions === true;

    const [getItemVersions] = useLazyGetItemVersionsQuery();
    const [getItemByUniqueIdVersions] = useLazyGetItemByUniqueIDVersionsQuery();
    const [restoreVersionByLabel] = useLazyRestoreVersionByLabelQuery();
    const [deleteVersionByLabel] = useLazyDeleteVersionByLabelQuery();
    const [deleteAllVersions] = useLazyDeleteAllVersionsQuery();
    const [unpublishItem] = useLazyUnpublishItemQuery();

    const [userPermissions, setUserPermissions] = React.useState<currentSharepointFolderUserPermissions>();
    const [fileVersions, setFileVersions] = React.useState<any[]>([]);
    const [itemVersions, setItemVersions] = React.useState<any[]>([]);
    const [columns, setColumns] = React.useState<IColumn[]>([]);
    const [commandbarItems, setCommandbarItems] = React.useState<ICommandBarItemProps[]>([]);
    const [commandbarFarItems, setCommandbarFarItems] = React.useState<ICommandBarItemProps[]>([]);

    const [selectedVersions, setSelectedVersions] = React.useState<any[]>([]);
    const [selectedVersion, setSelectedVersion] = React.useState<any>();

    const [updateVersionList, setUpdateVersionList] = React.useState<boolean>();
    const [waitFor, setWaitFor] = React.useState<boolean>(false);
    const [waitForText, setWaitForText] = React.useState<string>('');
    const [selectionCleared, setSelectionCleared] = React.useState<boolean>(false);

    const [showDeleteAllDialog, setShowDeleteAllDialog] = React.useState(false);
    const [showDeleteAllMinorDialog, setShowDeleteAllMinorDialog] = React.useState(false);

    const _selection = React.useRef(new Selection({
        onSelectionChanged: () => _getSelectionDetails()
    }));

    const _getSelectionDetails = () => {
        setSelectedVersions(_selection.current.getSelection());

        if (_selection.current.count === 1) {
            let version: any = _selection.current.getSelection()[0];
            setSelectedVersion(version);
        } else {
            setSelectedVersion(null);
        }
    };

    React.useEffect(() => {
        if (selectionCleared) {
            _selection.current.setAllSelected(false);
            setSelectedVersions(_selection.current.getSelection());
            setSelectedVersion(null);
            setSelectionCleared(false);
        }
    }, [selectionCleared]);

    React.useEffect(() => {
        (async () => {
            let permissions: currentSharepointFolderUserPermissions;
            if (selectedItem && selectedItem.PermMask) {
                permissions = getUserEffectivePermissionsFromMask(selectedItem.PermMask);
            }
            else if (selectedAddinItem && selectedAddinItem.PermMask) {
                permissions = getUserEffectivePermissionsFromMask(selectedAddinItem.PermMask);
            }

            if (!permissions) {
                const accessToken: string = isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken();
                const webUrl: string = targetWebUrl;
                const uniqueId: string = selectedItem ? selectedItem.UniqueId?.replace("{", "").replace("}", "") : selectedAddinItem.UniqueId;
                if (webUrl && targetListId && uniqueId) {
                    permissions = await getUserEffectivePermissions(accessToken, webUrl, targetListId, uniqueId);
                }
            }

            setUserPermissions(permissions);
        })();
    }, [selectedItem, selectedAddinItem, currentSharepointFolder]);

    React.useEffect(() => {
        (async () => {
            try {
                const [versions, versionsAlt] = await Promise.all([
                    getItemVersions({
                        siteUrl: targetWebUrl,
                        uniqueId: selectedItem ? selectedItem.UniqueId.replace("{", "").replace("}", "") : selectedAddinItem.UniqueId,
                        accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                    }).unwrap(),

                    getItemByUniqueIdVersions({
                        siteUrl: targetWebUrl,
                        sharePointListId: targetListId,
                        uniqueId: selectedItem ? selectedItem.UniqueId.replace("{", "").replace("}", "") : selectedAddinItem.UniqueId,
                        accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                    }).unwrap()
                ]);

                if (versions?.d?.results && versionsAlt?.value) {
                    const fileVersions = versions.d.results;
                    const itemVersions = versionsAlt.value
                        .map(item => {
                            let fileSize: number = fileVersions.find(fileVersion => fileVersion.VersionLabel === item.VersionLabel)?.Size;
                            if (isNaN(fileSize)) {
                                fileSize = Number(item.File_x005f_x0020_x005f_Size);
                            }
                            return { ...item, VersionLabelNumeric: Number(item.VersionLabel), FileSizeNumeric: fileSize };
                        });

                    setColumns([
                        {
                            key: 'version',
                            name: languageStrings.Version,
                            fieldName: 'VersionLabelNumeric',
                            minWidth: 65,
                            maxWidth: 65,
                            isResizable: true,
                            isSorted: true,
                            isSortedDescending: true,
                            onRender: (item: any) => {
                                return (
                                    <span>{item?.VersionLabel}</span>
                                )
                            }
                        },
                        {
                            key: 'modified',
                            name: languageStrings.Modified,
                            fieldName: 'Modified',
                            minWidth: 120,
                            maxWidth: 120,
                            isResizable: true,
                            onRender: (item: any) => {
                                let versionUrl: string;
                                const siteUrl: string = targetWebUrl;
                                const fileVersion = fileVersions.find(fileVersion => fileVersion.VersionLabel === item.VersionLabel);
                                if (fileVersion) {
                                    versionUrl = (!siteUrl.endsWith("/") ? siteUrl + "/" : siteUrl) + fileVersion.Url;
                                }
                                else {
                                    const siteOrigin: string = new URL(siteUrl)?.origin;
                                    versionUrl = (siteOrigin.endsWith("/") ? siteOrigin.slice(0, -1) : siteOrigin) + item.FileRef;
                                }
                                return (
                                    <>
                                        <Link href={versionUrl} target="_blank">{dayjs(item.Modified).format('L LT')}</Link>
                                        {targetEnableMinorVersions && item.IsCurrentVersion === true && (item.VersionId % 512) === 0 &&
                                            <div>
                                                <span>{languageStrings.PublishedMajorVersion}</span>
                                            </div>}
                                    </>
                                )
                            },
                        },
                        {
                            key: 'modifiedBy',
                            name: languageStrings.ModifiedBy,
                            minWidth: 120,
                            maxWidth: 120,
                            isResizable: true,
                            onRender: (item: any) => {
                                return (
                                    <span>{item?.Editor?.LookupValue}</span>
                                )
                            },
                        },
                        {
                            key: 'size',
                            name: languageStrings.Size,
                            fieldName: "FileSizeNumeric",
                            minWidth: 50,
                            maxWidth: 50,
                            isResizable: true,
                            onRender: (item: any) => {
                                return (
                                    <span>{formatBytes(item.FileSizeNumeric, 1)}</span>
                                )
                            },
                        },
                        {
                            key: 'comments',
                            name: 'Comments',
                            minWidth: 100,
                            maxWidth: 300,
                            isResizable: true,
                            onRender: (item: any) => {
                                let versionComment: string;
                                const fileVersion = fileVersions.find(fileVersion => fileVersion.VersionLabel === item.VersionLabel);
                                if (fileVersion) {
                                    versionComment = fileVersion.CheckInComment;
                                }
                                else {
                                    versionComment = item.OData__x005f_CheckinComment;
                                }

                                return (
                                    <span style={{ whiteSpace: "normal" }}>{versionComment}</span>
                                )
                            },
                        },
                    ]);

                    setFileVersions(fileVersions);
                    setItemVersions(itemVersions);
                }
            } catch (error) {
                logger(error, "CMVersionHistoryModal.tsx (init)");
            }

            setSelectionCleared(true);
        })();
    }, [updateVersionList]);

    React.useEffect(() => {
        (async () => {
            try {
                const items: ICommandBarItemProps[] = [];

                if (selectedVersions.length === 0 && userPermissions?.canDeleteVersions) {
                    items.push(
                        {
                            key: 'DeleteAll',
                            disabled: itemVersions?.length === 0 || waitFor === true,
                            text: languageStrings.VersionsDeleteAll,
                            // iconProps: { iconName: 'Delete' },
                            iconProps: {},
                            onRenderIcon: () => <Delete20Regular className={menuItemIconStyles} />,
                            onClick: () => {
                                setShowDeleteAllDialog(true);
                            }
                        })

                    if (targetEnableMinorVersions && userPermissions?.canDeleteVersions && itemVersions.some(x => !x.IsCurrentVersion && x.VersionId % 512 > 0)) {
                        items.push(
                            {
                                key: 'DeleteMinors',
                                disabled: itemVersions?.length === 0 || waitFor === true,
                                text: languageStrings.VersionsDeleteMinors,
                                // iconProps: { iconName: 'DependencyRemove' },
                                iconProps: {},
                                onRenderIcon: () => <HistoryDismiss20Regular className={menuItemIconStyles} />,
                                onClick: () => {
                                    setShowDeleteAllMinorDialog(true);
                                }
                            })
                    }
                }

                if (selectedVersions.length === 1) {
                    items.push(
                        {
                            key: 'view',
                            text: languageStrings.VersionView,
                            disabled: waitFor === true,
                            // iconProps: { iconName: 'OpenFile' },
                            iconProps: {},
                            onRenderIcon: () => <Open20Regular className={menuItemIconStyles} />,
                            onClick: () => {
                                let versionUrl: string;
                                const siteUrl: string = targetWebUrl;
                                const fileVersion = fileVersions.find(fileVersion => fileVersion.VersionLabel === selectedVersion.VersionLabel);
                                if (fileVersion) {
                                    versionUrl = (!siteUrl.endsWith("/") ? siteUrl + "/" : siteUrl) + fileVersion.Url;
                                }
                                else {
                                    const siteOrigin: string = new URL(siteUrl)?.origin;
                                    versionUrl = (siteOrigin.endsWith("/") ? siteOrigin.slice(0, -1) : siteOrigin) + selectedVersion.FileRef;
                                }

                                window.open(versionUrl, "_blank");
                            }
                        });

                    const isPublishedMajorVersion: boolean = selectedVersion.IsCurrentVersion === true && (selectedVersion.VersionId % 512) === 0;
                    const isPublishedMajorSucceededByDraft: boolean = isPublishedMajorVersion && itemVersions.find(ver => ver.IsCurrentVersion)?.VersionId > selectedVersion.VersionId;
                    if (userPermissions?.canEditListItem && (!selectedVersion.IsCurrentVersion || isPublishedMajorSucceededByDraft)) {
                        items.push({
                            key: 'restore',
                            disabled: waitFor === true,
                            text: languageStrings.VersionRestore,
                            // iconProps: { iconName: 'Refresh' },
                            iconProps: {},
                            onRenderIcon: () => <ArrowClockwise20Regular className={menuItemIconStyles} />,
                            onClick: () => {
                                restoreVersion();
                            }
                        });

                        if (!isPublishedMajorVersion && userPermissions?.canDeleteVersions) {
                            items.push(
                                {
                                    key: 'delete',
                                    disabled: waitFor === true,
                                    text: languageStrings.VersionDelete,
                                    // iconProps: { iconName: 'Delete' },
                                    iconProps: {},
                                    onRenderIcon: () => <Delete20Regular className={menuItemIconStyles} />,
                                    onClick: () => {
                                        deleteVersion();
                                    }
                                }
                            );
                        }
                    }

                    if (isPublishedMajorVersion && !isPublishedMajorSucceededByDraft && userPermissions?.canEditListItem) {
                        items.push(
                            {
                                key: 'unpublish',
                                disabled: waitFor === true,
                                name: languageStrings.Unpublish,
                                // iconProps: { iconName: "UnpublishContent" },
                                iconProps: {},
                                onRenderIcon: () => <StackOff20Regular className={menuItemIconStyles} />,
                                onClick: () => {
                                    onUnpublishVersion(selectedVersion);
                                }
                            }
                        )
                    }
                }

                if (selectedVersions.length > 1 && userPermissions?.canDeleteVersions) {
                    items.push(
                        {
                            key: 'deletemulti',
                            disabled: waitFor === true,
                            text: languageStrings.VersionDelete,
                            // iconProps: { iconName: 'Delete' },
                            iconProps: {},
                            onRenderIcon: () => <Delete20Regular className={menuItemIconStyles} />,
                            onClick: () => {
                                deleteVersion();
                            }
                        }
                    );
                }

                setCommandbarItems(items)

                const farItems: ICommandBarItemProps[] = [];
                if (waitFor) {
                    farItems.push(
                        {
                            key: "loadingFiles",
                            text: waitForText,
                            disabled: true,
                            onRenderIcon: () => {
                                return <Spinner size={SpinnerSize.small} style={{ paddingRight: 5 }} />
                            }
                        }
                    )
                }

                if (selectedVersions.length > 0) {
                    farItems.push(
                        {
                            key: "selectedFileCount",
                            text: `${selectedVersions.length} ${languageStrings.Selected.toLowerCase()}`,
                            disabled: waitFor === true,
                            // iconProps: { iconName: "Clear" },
                            iconProps: {},
                            onRenderIcon: () => <Dismiss20Regular className={menuItemIconStyles} />,
                            onClick: () => {
                                setSelectionCleared(true);
                            }
                        }
                    )
                }

                setCommandbarFarItems(farItems)

            } catch (error) {
                logger(error, "CMVersionHistoryModal.tsx (Commandbar init)");
            }
        })();
    }, [selectedVersions, fileVersions, itemVersions, waitFor, currentSharepointFolder, userPermissions]);

    const restoreVersion = async () => {
        setWaitForText(`${languageStrings.Restoring}...`);
        setWaitFor(true);

        try {
            const results = await restoreVersionByLabel({
                siteUrl: targetWebUrl,
                uniqueId: selectedItem ? selectedItem.UniqueId.replace("{", "").replace("}", "") : selectedAddinItem.UniqueId,
                accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                versionLabel: selectedVersion.VersionLabel
            }).unwrap();
        } catch (error) {
            logger(error, "CMVersionHistoryModal.tsx (restoreVersion)");
        }

        setWaitFor(false);
        setWaitForText('')
        setUpdateVersionList(prev => prev ? !prev : true);
    }

    const deleteVersion = async () => {
        setWaitForText(`${languageStrings.Deleting}...`);
        setWaitFor(true);

        try {
            await Promise.all(selectedVersions.map(async version => {
                if (!version.IsCurrentVersion) {
                    await deleteVersionByLabel({
                        siteUrl: targetWebUrl,
                        uniqueId: selectedItem ? selectedItem.UniqueId.replace("{", "").replace("}", "") : selectedAddinItem.UniqueId,
                        accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                        versionLabel: version.VersionLabel
                    }).unwrap();
                }
            }));
        } catch (error) {
            logger(error, "CMVersionHistoryModal.tsx (deleteVersion)");
        }

        setWaitFor(false);
        setWaitForText('')
        setUpdateVersionList(prev => prev ? !prev : true);
    }

    const onUnpublishVersion = async (item: any) => {
        setWaitFor(true);

        try {
            await unpublishItem({
                siteUrl: targetWebUrl,
                uniqueId: item.UniqueId.replace("{", "").replace("}", ""),
                accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
            }).unwrap();
        } catch (error) {
            logger(error, "CMVersionHistoryModal.tsx (onUnpublishVersion)");
        }

        setWaitFor(false);
        setUpdateVersionList(prev => prev ? !prev : true);
    }

    const onDeleteAllVersions = async () => {
        setWaitForText(`${languageStrings.Deleting}...`);
        setWaitFor(true);

        try {
            const results = await deleteAllVersions({
                siteUrl: targetWebUrl,
                uniqueId: selectedItem ? selectedItem.UniqueId.replace("{", "").replace("}", "") : selectedAddinItem.UniqueId,
                accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken()
            }).unwrap();
        } catch (error) {
            logger(error, "CMVersionHistoryModal.tsx (onDeleteAllVersions)");
        }

        setWaitFor(false);
        setWaitForText('')
        setUpdateVersionList(prev => prev ? !prev : true);
    }

    const onDeleteAllMinorVersions = async () => {
        setWaitForText(`${languageStrings.Deleting}...`);
        setWaitFor(true);

        try {
            await Promise.all(itemVersions.map(async version => {
                if (!version.IsCurrentVersion && version.VersionId % 512 > 0) {
                    await deleteVersionByLabel({
                        siteUrl: targetWebUrl,
                        uniqueId: selectedItem ? selectedItem.UniqueId.replace("{", "").replace("}", "") : selectedAddinItem.UniqueId,
                        accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                        versionLabel: version.VersionLabel
                    }).unwrap();
                }
            }));
        } catch (error) {
            logger(error, "CMVersionHistoryModal.tsx (onDeleteAllMinorVersions)");
        }

        setWaitFor(false);
        setWaitForText('')
        setUpdateVersionList(prev => prev ? !prev : true);
    }

    const onColumnHeaderClick = React.useCallback((ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
        const newColumns: IColumn[] = columns.slice();
        const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
        newColumns.forEach((newCol: IColumn) => {
            if (newCol === currColumn) {
                currColumn.isSortedDescending = !currColumn.isSortedDescending;
                currColumn.isSorted = true;
            } else {
                newCol.isSorted = false;
                newCol.isSortedDescending = true;
            }
        });
        setColumns(newColumns);
        setItemVersions(_copyAndSort(itemVersions, currColumn.fieldName!, currColumn.isSortedDescending));
    }, [columns, itemVersions, setColumns, setItemVersions]);

    const dialogContentProps: IDialogContentProps = {
        type: DialogType.normal,
        title: languageStrings.VersionHistory,
        showCloseButton: true,
        closeButtonAriaLabel: 'Close',
    };

    return (
        <React.Fragment>
            {selectedItem && <Dialog hidden={!showDialog} onDismiss={() => closeDialog()} dialogContentProps={dialogContentProps} minWidth={800} maxWidth={800}>
                <DialogContent styles={{ inner: { padding: 0, height: 350 }, header: { display: 'none' } }}>
                    <CommandBar
                        styles={commandBarStyle}
                        items={commandbarItems}
                        farItems={commandbarFarItems}
                        ariaLabel="Use left and right arrow keys to navigate between commands"
                    />
                    <div className={classNames.wrapper}>
                        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                            <DetailsList
                                compact={true}
                                items={itemVersions}
                                columns={columns}
                                setKey="set"
                                layoutMode={DetailsListLayoutMode.justified}
                                constrainMode={ConstrainMode.unconstrained}
                                selection={_selection.current}
                                selectionPreservedOnEmptyClick={true}
                                onColumnHeaderClick={onColumnHeaderClick}
                                getKey={_getKey}
                                ariaLabelForSelectionColumn="Toggle selection"
                                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                                checkButtonAriaLabel="select row"
                            />
                        </ScrollablePane>
                    </div>
                </DialogContent>
                {showDeleteAllDialog && <CMConfirmModal
                    title={languageStrings.Delete}
                    message={languageStrings.VersionConfirmDeleteAllMessage}
                    showDialog={showDeleteAllDialog}
                    closeDialog={() => setShowDeleteAllDialog(false)}
                    closeText={languageStrings.Cancel}
                    confirmText={languageStrings.Yes}
                    onConfirm={() => { onDeleteAllVersions(); setShowDeleteAllDialog(false) }}
                />}
                {showDeleteAllMinorDialog && <CMConfirmModal
                    title={languageStrings.Delete}
                    message={languageStrings.VersionConfirmDeleteAllMinorMessage}
                    showDialog={showDeleteAllMinorDialog}
                    closeDialog={() => setShowDeleteAllMinorDialog(false)}
                    closeText={languageStrings.Cancel}
                    confirmText={languageStrings.Yes}
                    onConfirm={() => { onDeleteAllMinorVersions(); setShowDeleteAllMinorDialog(false) }}
                />}
            </Dialog>}
            {selectedAddinItem && <Panel
                isOpen={showDialog}
                headerText={languageStrings.VersionHistory}
                onDismiss={() => closeDialog()}
                isLightDismiss
                closeButtonAriaLabel="Close"
                type={PanelType.custom}
                customWidth={!generalModule.isInConentMangerMode() ? "300px" : "450px"}
                styles={panelStyles}
            >
                <CommandBar
                    styles={commandBarStyle}
                    items={commandbarItems}
                    farItems={commandbarFarItems}
                    ariaLabel="Use left and right arrow keys to navigate between commands"
                />
                <div className={classNames.wrapper}>
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                        <DetailsList
                            compact={true}
                            items={itemVersions}
                            columns={columns}
                            setKey="set"
                            layoutMode={DetailsListLayoutMode.justified}
                            selection={_selection.current}
                            selectionPreservedOnEmptyClick={true}
                            onColumnHeaderClick={onColumnHeaderClick}
                            getKey={_getKey}
                            ariaLabelForSelectionColumn="Toggle selection"
                            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                            checkButtonAriaLabel="select row"
                        />
                    </ScrollablePane>
                </div>
            </Panel>}
        </React.Fragment>
    )
};